<template>
  <Modal
    title="Send form to customer"
    class-name="small-modal"
    :saving="sending"
    saving-text="Sending..."
    submit-btn-text="Send"
    :disable-submit="!canSend"
    @submit="send"
    @hide-modal="emit('hide-modal')"
  >
    <!-- The presence hook sits on this wrapper rather than on Modal: Modal
         renders through SafeTeleport, so a data-cy passed to it as an attribute
         never reaches the DOM. -->
    <div data-cy="cf-send-modal">
      <p class="mb-3 text-sm text-gray-700" data-cy="cf-send-form-name">
        {{ submission.name }} will be emailed as a PDF attachment.
      </p>

      <p v-if="loading" class="text-sm text-gray-500" data-cy="cf-send-loading">
        Loading recipients…
      </p>

      <template v-else>
        <!-- A failed lookup and an empty one both leave the list blank, and the
             two need different words: saying "no email on file" when the request
             errored states something that may be untrue, and the send that
             follows would then be refused for naming none of the job's own
             addresses — a reason that contradicts what this box just said. -->
        <p
          v-if="loadFailed"
          class="mb-3 text-sm text-gray-700"
          data-cy="cf-send-recipients-failed"
        >
          Could not load this customer's email addresses.
          <button
            type="button"
            class="text-blue-600 hover:underline"
            data-cy="cf-send-retry"
            @click="loadRecipients"
          >
            Try again
          </button>
        </p>

        <!-- Nothing on file, so there is no list to check and the send needs an
             address typed for this one time. Seeded with a single empty row so
             the field is present and flagged the moment the modal opens. -->
        <p
          v-else-if="!onFileRecipients.length"
          class="mb-3 text-sm text-gray-700"
          data-cy="cf-send-no-email-on-file"
        >
          This customer has no email address on file. Enter one to send this
          form. It is used for this send only and is not saved to the customer.
        </p>

        <RecipientList
          v-if="customerRecipients.length"
          title="Customer Emails:"
          :recipients="customerRecipients"
          data-cy="cf-send-customer-recipients"
          @toggle-checked="toggleRecipient"
        />
        <RecipientList
          v-if="addressRecipients.length"
          class="mt-4"
          title="Address Emails:"
          :recipients="addressRecipients"
          data-cy="cf-send-address-recipients"
          @toggle-checked="toggleRecipient"
        />

        <div v-if="!loadFailed" class="mt-4" data-cy="cf-send-additional">
          <div v-if="onFileRecipients.length">
            <strong class="text-sm">Additional Emails:</strong>
          </div>
          <AdditionalRecipient
            v-for="(_, index) in additionalEmails"
            ref="additionalEmailRefs"
            :key="index"
            v-model="additionalEmails[index]"
            class="mb-1"
            data-cy="cf-send-additional-recipient"
            @remove-recipient="removeAdditional(index)"
          />
          <ItemAdder
            v-if="additionalEmails.length < MAX_ADDITIONAL"
            text="Add Email"
            class="mt-2"
            slim
            transparent
            data-cy="cf-send-add-email"
            @click="addAdditional"
          />
        </div>
      </template>
    </div>
  </Modal>
</template>

<script setup lang="ts">
// Bespoke rather than EmailQuoteOrInvoiceModal: that modal branches on a quote
// or an invoice from its props down through its composable and its four vuex
// actions, and it addresses the server through a `links` object the custom form
// contract does not carry. Its two recipient components are prop-driven and
// entity-agnostic despite where they live, so those are reused as they are.
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import ItemAdder from "@/components/ItemAdder.vue";
import Modal from "@/components/Modal.vue";
import AdditionalRecipient from "@/EmailQuoteOrInvoice/Recipients/AdditionalRecipient.vue";
import RecipientList from "@/EmailQuoteOrInvoice/Recipients/RecipientList.vue";
import { EmailRecipient } from "@/EmailQuoteOrInvoice/types";
import type { CustomFormSubmission } from "@/typings/customForm";

const MAX_ADDITIONAL = 10;

const props = defineProps<{
  submission: CustomFormSubmission;
  // Absent on a job with no service address. The send endpoint copes — it falls
  // back to an empty list of its own — but the contact endpoint is keyed by
  // both, so the modal skips the lookup and asks for an address instead.
  customerId?: number;
  addressId?: number;
}>();

const emit = defineEmits<{
  "hide-modal": [];
}>();

const store = useStore();
const loading = ref(true);
const loadFailed = ref(false);
const sending = ref(false);
const onFileRecipients = ref<EmailRecipient[]>([]);
const additionalEmails = ref<string[]>([]);
const additionalEmailRefs = ref<{ validate: () => Promise<boolean> }[] | null>(
  null
);
const customerRecipients = computed(() =>
  onFileRecipients.value.filter((recipient) => recipient.owner === "Customer")
);

const addressRecipients = computed(() =>
  onFileRecipients.value.filter((recipient) => recipient.owner !== "Customer")
);

const checkedOnFile = computed(() =>
  onFileRecipients.value.filter((recipient) => recipient.checked)
);

const enteredEmails = computed(() =>
  additionalEmails.value.map((email) => email.trim()).filter(Boolean)
);

// The server takes a typed address on its own only when the job holds none of
// its own; with addresses on file it requires one of them in the list. Mirroring
// that here is what keeps Send from being a button that always 422s.
const canSend = computed(() => {
  if (loading.value || loadFailed.value) return false;
  if (onFileRecipients.value.length) return checkedOnFile.value.length > 0;
  return enteredEmails.value.length > 0;
});

function toggleRecipient(recipient: EmailRecipient) {
  recipient.checked = !recipient.checked;
}

function addAdditional() {
  additionalEmails.value.push("");
}

// With nothing on file these fields are the only way to name a recipient, so
// removing the last one would leave a modal that can never send. It stays,
// emptied.
function removeAdditional(index: number) {
  additionalEmails.value.splice(index, 1);
  if (!onFileRecipients.value.length && !additionalEmails.value.length) {
    additionalEmails.value = [""];
  }
}

async function additionalEmailsValid(): Promise<boolean> {
  const rows = additionalEmailRefs.value ?? [];
  const results = await Promise.all(rows.map((row) => row.validate()));
  return results.every(Boolean);
}

async function loadRecipients() {
  loading.value = true;
  loadFailed.value = false;

  if (!props.customerId || !props.addressId) {
    additionalEmails.value = [""];
    loading.value = false;
    return;
  }

  try {
    const contacts = await store.dispatch("fetchCustomFormEmailRecipients", {
      customerId: props.customerId,
      addressId: props.addressId
    });
    onFileRecipients.value = contacts.map(
      (contact: {
        email_address: string;
        label: string | null;
        owner: string;
      }) =>
        new EmailRecipient({
          owner: contact.owner as EmailRecipient["owner"],
          name: contact.label ?? "",
          email_address: contact.email_address,
          checked: true
        })
    );
    // AC3: no list to check means the address has to be typed, so the field is
    // there and flagged as required from the moment the modal opens.
    if (!onFileRecipients.value.length) additionalEmails.value = [""];
  } catch {
    // The interceptor has already flashed the server's reason; what this needs to
    // do is stop the modal claiming the list is empty when it never arrived.
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

// On failure the global response interceptor already shows the endpoint's
// message, so the catch here only keeps the modal open — toasting again would
// show the same error twice.
async function send() {
  if (!canSend.value || sending.value) return;
  if (!(await additionalEmailsValid())) return;

  sending.value = true;
  try {
    const emails = await store.dispatch("emailJobCustomFormSubmission", {
      jobId: props.submission.job_id,
      id: props.submission.id,
      emails: [
        ...checkedOnFile.value.map((recipient) => recipient.email_address),
        ...enteredEmails.value
      ]
    });
    window.notify?.success(`Form sent to ${(emails ?? []).join(", ")}`);
    emit("hide-modal");
  } catch {
    // See above.
  } finally {
    sending.value = false;
  }
}

onMounted(loadRecipients);
</script>
